/**
 * Feature flags — `/v1/admin/flags*`.
 *
 * **Every admin scope reads; who may flip is decided per flag by the server.** The row carries
 * `editable_by`, so the screen can show a disabled control with the reason rather than letting
 * somebody discover the refusal by trying — but the client never decides it. A flip the client
 * thought was allowed still goes to the server, and the server's 403 is what the user sees.
 *
 * **`evaluations_30d` can be `null`**, and null is not zero. It means the analytics pipeline
 * has not answered, and rendering it as `0` would say "nothing uses this flag" — the sentence
 * that gets a live capability deleted.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type FlagRow = components['schemas']['FlagRow'];
export type FlagListResponse = components['schemas']['FlagListResponse'];
export type FlagReportResponse = components['schemas']['FlagReportResponse'];
export type FlagRevisionRow = components['schemas']['FlagRevisionRow'];
export type FlagRevisionsResponse = components['schemas']['FlagRevisionsResponse'];
export type FlagFlipIn = components['schemas']['FlagFlipIn'];
export type FlagPatchIn = components['schemas']['FlagPatchIn'];
export type FlagCreateIn = components['schemas']['FlagCreateIn'];
export type InternalTesterRow = components['schemas']['InternalTesterRow'];
type InternalTestersResponse = components['schemas']['InternalTestersResponse'];

export function useFlags() {
  return useQuery({
    queryKey: keys.flags.list(),
    queryFn: () => authedClient.request<FlagListResponse>('/v1/admin/flags'),
  });
}

export function useFlagReport() {
  return useQuery({
    queryKey: keys.flags.report(),
    queryFn: () => authedClient.request<FlagReportResponse>('/v1/admin/flags/report'),
  });
}

/** Enabled only once a flag is open, so the list screen does not fetch history it never shows. */
export function useFlagRevisions(flagKey: string | null) {
  /* Narrowed once. Coalescing again inside `queryFn` would be a branch the `enabled` guard
     makes unreachable — and an unreachable branch is a claim nobody can check. */
  const key = flagKey ?? '';
  return useQuery({
    queryKey: keys.flags.revisions(key),
    queryFn: () =>
      authedClient.request<FlagRevisionsResponse>(
        `/v1/admin/flags/${encodeURIComponent(key)}/revisions`,
      ),
    enabled: key !== '',
  });
}

function useFlagMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.flags.all() }),
  });
}

/** The one act that changes what is live. The reason is required by the server, not by us. */
export function useFlipFlag() {
  return useFlagMutation(({ flagKey, ...body }: FlagFlipIn & { flagKey: string }) =>
    authedClient.request<FlagRow>(`/v1/admin/flags/${encodeURIComponent(flagKey)}/flip`, {
      method: 'POST',
      body,
    }),
  );
}

export function useUpdateFlag() {
  return useFlagMutation(({ flagKey, ...body }: FlagPatchIn & { flagKey: string }) =>
    authedClient.request<FlagRow>(`/v1/admin/flags/${encodeURIComponent(flagKey)}`, {
      method: 'PATCH',
      body,
    }),
  );
}

export function useCreateFlag() {
  return useFlagMutation((body: FlagCreateIn) =>
    authedClient.request<FlagRow>('/v1/admin/flags', { method: 'POST', body }),
  );
}

export function useInternalTesters() {
  return useQuery({
    queryKey: keys.flags.testers(),
    queryFn: () => authedClient.request<InternalTestersResponse>('/v1/admin/flags/internal-testers'),
  });
}

/** The whole list, every time — removing somebody is sending the list without them. */
export function useSetInternalTesters() {
  return useFlagMutation((userIds: number[]) =>
    authedClient.request<InternalTestersResponse>('/v1/admin/flags/internal-testers', {
      method: 'PUT',
      body: { user_ids: userIds },
    }),
  );
}
